import {
  makeCurrencyFormatter,
  percentageFormatter,
  percentageNoSymbolFormatter,
  scaledPercentageFormatter,
  numberFormatter,
} from "helpers/formatters/numbers"
import { VALUE_FORMATS } from "./constants"
import { NIL_DISPLAY } from "./nil-display"

function withNullHandling(formatter) {
  return (value) => (value == null ? NIL_DISPLAY : formatter(value))
}

/**
 * Gets a formatter for a given VALUE_FORMATS type.
 *
 * @param {string} format - VALUE_FORMATS constant
 * @param {Object} options - Formatter options
 * @param {boolean} options.includeUnits - If true, includes units (%). Default: true for tooltips. CURRENCY always includes its own symbol or code regardless of this flag (see valueOnly).
 * @param {boolean} options.handleNull - If true, returns "-" for null/undefined. Default: false.
 * @param {string} [options.forcedScale] - CURRENCY only. Forces every value onto this scale ("K" or "M") instead of auto-picking one per value.
 * @param {boolean} options.valueOnly - CURRENCY only. If true, omits the currency symbol and scale suffix. Default: false. Only takes effect together with `forcedScale`.
 * @param {string} [options.currency] - CURRENCY only. ISO 4217 code, e.g. "EUR". Undefined, empty or "USD" formats as US dollars.
 * @param {boolean} [options.displayCurrencySymbol] - CURRENCY only. If false, omits the currency symbol. Default: true.
 * @returns {Function} Formatter function
 */
export function getFormatter(
  format,
  { includeUnits = true, handleNull = false, forcedScale, valueOnly = false, currency, displayCurrencySymbol = true } = {},
) {
  let formatter

  switch (format) {
    case VALUE_FORMATS.PERCENT:
      formatter = includeUnits ? percentageFormatter : percentageNoSymbolFormatter
      break
    case VALUE_FORMATS.PERCENT_100:
      formatter = includeUnits ? scaledPercentageFormatter : numberFormatter
      break
    case VALUE_FORMATS.NUMBER:
      formatter = numberFormatter
      break
    case VALUE_FORMATS.CURRENCY:
      formatter = makeCurrencyFormatter(currency || undefined, { displayCurrencySymbol, forcedScale, valueOnly })
      break
    case VALUE_FORMATS.STRING:
    default:
      formatter = String
  }

  return handleNull ? withNullHandling(formatter) : formatter
}
